/**
 * Hook pour gérer le contexte Office.js dans la sidebar
 * Détecte l'application hôte (Outlook/Word/Excel) et extrait le contexte courant
 */
import { useState, useEffect, useCallback } from 'react';
import {
    buildDesktopReturnUrls,
    type OfficePlatformType,
} from './microsoftDesktopLaunch';

export type OfficeHostType = 'Outlook' | 'Word' | 'Excel' | 'Unknown';

export interface OfficeContextData {
    hostApp: OfficeHostType;
    hostPlatform: OfficePlatformType;
    contextText: string;
    contextType: 'email-thread' | 'document' | 'excel-matrix' | 'unknown';
    subject?: string;
    documentTitle?: string;
    cellRange?: string;
    emailBody?: string;
    recipients?: string[];
    outlookItemId?: string;
    conversationId?: string;
    desktopReturnUrl?: string;
}

interface UseOfficeContextReturn {
    isOfficeReady: boolean;
    isLoading: boolean;
    error: string | null;
    context: OfficeContextData | null;
    refreshContext: () => Promise<void>;
}

const HOST_TYPE_MAP: Record<number, OfficeHostType> = {
    0: 'Word',
    1: 'Excel',
    2: 'Outlook',
};

function resolveHostPlatform(): OfficePlatformType {
    if (typeof Office === 'undefined') {
        return 'Unknown';
    }

    const platform = Office.context.platform;
    if (!platform) {
        return 'Unknown';
    }

    const platformValue = String(platform) as OfficePlatformType;
    if (
        platformValue === 'OfficeOnline'
        || platformValue === 'PC'
        || platformValue === 'Mac'
        || platformValue === 'iOS'
        || platformValue === 'Android'
        || platformValue === 'Universal'
    ) {
        return platformValue;
    }

    return 'Unknown';
}

function resolveHostApp(hostType: Office.HostType | number | undefined): OfficeHostType {
    if (typeof hostType === 'number') {
        return HOST_TYPE_MAP[hostType] || 'Unknown';
    }

    if (hostType) {
        return String(hostType) as OfficeHostType;
    }

    return 'Unknown';
}

function enrichWithReturnUrls(
    contextData: OfficeContextData,
    hostApp: OfficeHostType
): OfficeContextData {
    const returnUrls = buildDesktopReturnUrls(hostApp, contextData.outlookItemId);

    return {
        ...contextData,
        desktopReturnUrl: returnUrls.desktopReturnUrl ?? undefined,
    };
}

export function useOfficeContext(): UseOfficeContextReturn {
    const [isOfficeReady, setIsOfficeReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [context, setContext] = useState<OfficeContextData | null>(null);

    const extractOutlookContext = useCallback(async (): Promise<OfficeContextData> => {
        return new Promise((resolve, reject) => {
            try {
                const mailbox = Office.context.mailbox;
                const item = mailbox?.item;
                const hostPlatform = resolveHostPlatform();

                if (!item) {
                    resolve(enrichWithReturnUrls({
                        hostApp: 'Outlook',
                        hostPlatform,
                        contextText: 'No email selected',
                        contextType: 'email-thread',
                    }, 'Outlook'));
                    return;
                }

                const subject = item.subject || 'Untitled';
                const outlookItemId = item.itemId;
                const conversationId = item.conversationId;

                const toRecipients = item.to?.map(
                    (r: Office.EmailAddressDetails) => r.emailAddress
                ) || [];
                const ccRecipients = item.cc?.map(
                    (r: Office.EmailAddressDetails) => r.emailAddress
                ) || [];

                const baseContext: OfficeContextData = {
                    hostApp: 'Outlook',
                    hostPlatform,
                    contextText: subject,
                    contextType: 'email-thread',
                    subject,
                    outlookItemId,
                    conversationId,
                    recipients: [...toRecipients, ...ccRecipients],
                };

                const finishWithBody = (emailBody?: string, selectedText?: string) => {
                    const selection = selectedText?.trim();
                    if (selection) {
                        resolve(enrichWithReturnUrls({
                            ...baseContext,
                            contextText: selection.length > 200
                                ? `${selection.slice(0, 200)}…`
                                : selection,
                            emailBody: selection,
                        }, 'Outlook'));
                        return;
                    }

                    resolve(enrichWithReturnUrls({
                        ...baseContext,
                        emailBody,
                    }, 'Outlook'));
                };

                const loadBodyThenResolve = (selectedText?: string) => {
                    item.body.getAsync(Office.CoercionType.Text, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            finishWithBody(result.value, selectedText);
                        } else {
                            finishWithBody(undefined, selectedText);
                        }
                    });
                };

                // PRD inline layer (pragmatic): prefer selected text when available
                if (typeof item.getSelectedDataAsync === 'function') {
                    item.getSelectedDataAsync(Office.CoercionType.Text, (selResult) => {
                        if (
                            selResult.status === Office.AsyncResultStatus.Succeeded
                            && selResult.value?.trim()
                        ) {
                            finishWithBody(undefined, selResult.value);
                        } else {
                            loadBodyThenResolve();
                        }
                    });
                } else {
                    loadBodyThenResolve();
                }
            } catch (err) {
                reject(err);
            }
        });
    }, []);

    const extractWordContext = useCallback(async (): Promise<OfficeContextData> => {
        return new Promise((resolve, reject) => {
            try {
                const hostPlatform = resolveHostPlatform();

                Word.run(async (ctx) => {
                    const doc: any = ctx.document;
                    const properties = doc.properties;
                    properties.load('title');

                    const selection = doc.getSelection();
                    selection.load('text');

                    const body = doc.body;
                    body.load('text');

                    await ctx.sync();

                    const title = properties.title || 'Untitled Document';
                    const selectedText = selection.text?.trim() || '';

                    let contextText = selectedText;
                    if (!contextText) {
                        const bodyText = (body.text || '').trim();
                        contextText = bodyText.slice(0, 50000);
                    }

                    resolve(enrichWithReturnUrls({
                        hostApp: 'Word',
                        hostPlatform,
                        contextText: contextText || title,
                        contextType: 'document',
                        documentTitle: title,
                        emailBody: contextText,
                    }, 'Word'));
                }).catch(reject);
            } catch (err) {
                reject(err);
            }
        });
    }, []);

    const extractExcelContext = useCallback(async (): Promise<OfficeContextData> => {
        return new Promise((resolve, reject) => {
            try {
                const hostPlatform = resolveHostPlatform();

                Excel.run(async (ctx) => {
                    const workbook: any = ctx.workbook;
                    const range = workbook.getSelectedRange();
                    range.load('address');
                    range.load('values');

                    await ctx.sync();

                    let address = range.address || 'No selection';
                    let values = range.values || [];

                    const flatValues = values
                        .flat()
                        .filter((v: any) => v !== null && v !== undefined && String(v).trim() !== '');

                    if (flatValues.length <= 1) {
                        try {
                            const usedRange = workbook.worksheets
                                .getActiveWorksheet()
                                .getUsedRange();
                            usedRange.load('address');
                            usedRange.load('values');
                            await ctx.sync();

                            if (usedRange.address) {
                                address = usedRange.address;
                                values = usedRange.values || values;
                            }
                        } catch {
                            // used range unavailable, keep selection data
                        }
                    }

                    const cellSummary = (values as any[][])
                        .flat()
                        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
                        .slice(0, 20)
                        .map((value) => String(value))
                        .join(', ');

                    resolve(enrichWithReturnUrls({
                        hostApp: 'Excel',
                        hostPlatform,
                        contextText: address,
                        contextType: 'excel-matrix',
                        cellRange: address,
                        emailBody: cellSummary || undefined,
                    }, 'Excel'));
                }).catch(reject);
            } catch (err) {
                reject(err);
            }
        });
    }, []);

    const refreshContext = useCallback(async () => {
        if (!isOfficeReady) return;

        setIsLoading(true);
        setError(null);

        try {
            const hostApp = resolveHostApp(Office.context.host);
            const hostPlatform = resolveHostPlatform();

            let contextData: OfficeContextData;

            switch (hostApp) {
                case 'Outlook':
                    contextData = await extractOutlookContext();
                    break;
                case 'Word':
                    contextData = await extractWordContext();
                    break;
                case 'Excel':
                    contextData = await extractExcelContext();
                    break;
                default:
                    contextData = enrichWithReturnUrls({
                        hostApp: 'Unknown',
                        hostPlatform,
                        contextText: 'Unknown application',
                        contextType: 'unknown',
                    }, 'Unknown');
            }

            setContext(contextData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to extract context');
            setContext(enrichWithReturnUrls({
                hostApp: 'Unknown',
                hostPlatform: resolveHostPlatform(),
                contextText: 'Error loading context',
                contextType: 'unknown',
            }, 'Unknown'));
        } finally {
            setIsLoading(false);
        }
    }, [isOfficeReady, extractOutlookContext, extractWordContext, extractExcelContext]);

    useEffect(() => {
        const initOffice = async () => {
            try {
                if (typeof Office !== 'undefined' && Office.onReady) {
                    await Office.onReady();
                    setIsOfficeReady(true);
                } else {
                    setError('Office.js not available');
                    setIsLoading(false);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Office initialization failed');
                setIsLoading(false);
            }
        };

        initOffice();
    }, []);

    useEffect(() => {
        if (isOfficeReady) {
            refreshContext();
        }
    }, [isOfficeReady, refreshContext]);

    return {
        isOfficeReady,
        isLoading,
        error,
        context,
        refreshContext,
    };
}

export default useOfficeContext;
